import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, FileText, MapPin, ChevronRight, CheckCircle2, AlertTriangle, ShieldAlert, Clock, Trash2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const STATUS_META = {
  "analisi-verde": { icon: CheckCircle2, text: "text-emerald-400", label: "Conforme", dot: "#00E676" },
  "analisi-giallo": { icon: AlertTriangle, text: "text-amber-400", label: "Tolleranza", dot: "#FFC400" },
  "analisi-rosso": { icon: ShieldAlert, text: "text-rose-400", label: "Difformità", dot: "#FF1744" },
  bozza: { icon: Clock, text: "text-slate-400", label: "Bozza", dot: "#64748B" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titolo: "", indirizzo: "" });

  const { data: pratiche = [], isLoading } = useQuery({
    queryKey: ["pratiche"],
    queryFn: async () => (await api.get("/pratiche")).data,
  });

  const createMut = useMutation({
    mutationFn: async (body) => (await api.post("/pratiche", body)).data,
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["pratiche"] });
      toast.success("Pratica creata");
      setOpen(false);
      setForm({ titolo: "", indirizzo: "" });
      navigate(`/pratica/${p.id}`);
    },
    onError: () => toast.error("Errore nella creazione"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/pratiche/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pratiche"] });
      toast.success("Pratica eliminata");
    },
  });

  const filtered = pratiche.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.titolo?.toLowerCase().includes(q) || p.indirizzo?.toLowerCase().includes(q);
    const matchFilter = filter === "all" || p.status === `analisi-${filter}`;
    return matchSearch && matchFilter;
  });

  const stats = {
    totale: pratiche.length,
    verde: pratiche.filter((p) => p.status === "analisi-verde").length,
    giallo: pratiche.filter((p) => p.status === "analisi-giallo").length,
    rosso: pratiche.filter((p) => p.status === "analisi-rosso").length,
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Pratiche di Conformità</h1>
          <p className="text-slate-400 mt-2">Gestisci le verifiche di stato legittimo e conformità edilizia.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="new-pratica-button"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-cyan-500 text-slate-900 font-semibold hover:bg-cyan-400 transition-colors"
            >
              <Plus className="h-5 w-5" /> Nuova Pratica
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#121824] border-slate-800 text-slate-100">
            <DialogHeader>
              <DialogTitle className="font-heading">Nuova Pratica di Conformità</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm text-slate-400">Titolo / Riferimento immobile</label>
                <input
                  data-testid="new-pratica-titolo-input"
                  value={form.titolo}
                  onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                  placeholder="Es. Appartamento Via Roma 12"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Indirizzo</label>
                <input
                  data-testid="new-pratica-indirizzo-input"
                  value={form.indirizzo}
                  onChange={(e) => setForm({ ...form, indirizzo: e.target.value })}
                  placeholder="Via, Città, Provincia"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-[#0B0F17] border border-slate-700 focus:border-cyan-500 outline-none text-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <button
                data-testid="create-pratica-confirm-button"
                disabled={!form.titolo || createMut.isPending}
                onClick={() => createMut.mutate(form)}
                className="w-full px-4 py-2.5 rounded-lg bg-cyan-500 text-slate-900 font-semibold hover:bg-cyan-400 transition-colors disabled:opacity-50"
              >
                {createMut.isPending ? "Creazione…" : "Crea Pratica"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {[
          { k: "totale", label: "Totale pratiche", val: stats.totale, color: "#00B4D8" },
          { k: "verde", label: "Conformi", val: stats.verde, color: "#00E676" },
          { k: "giallo", label: "In tolleranza", val: stats.giallo, color: "#FFC400" },
          { k: "rosso", label: "Difformi", val: stats.rosso, color: "#FF1744" },
        ].map((s) => (
          <div key={s.k} className="p-4 rounded-xl bg-[#121824] border border-slate-800">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
            <p className="font-heading text-3xl font-bold mt-2" style={{ color: s.color }}>{s.val}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            data-testid="dashboard-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per titolo o indirizzo…"
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#121824] border border-slate-800 focus:border-cyan-500 outline-none text-sm"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="filter-traffic-light-select" className="w-full sm:w-52 bg-[#121824] border-slate-800 text-slate-200">
            <SelectValue placeholder="Filtra esito" />
          </SelectTrigger>
          <SelectContent className="bg-[#121824] border-slate-800 text-slate-200">
            <SelectItem value="all">Tutti gli esiti</SelectItem>
            <SelectItem value="verde">Verde · Conforme</SelectItem>
            <SelectItem value="giallo">Giallo · Tolleranza</SelectItem>
            <SelectItem value="rosso">Rosso · Difformità</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 rounded-full border-2 border-cyan-500/30 border-t-cyan-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 rounded-xl border border-dashed border-slate-800 bg-[#121824]/50">
          <FileText className="h-10 w-10 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Nessuna pratica trovata.</p>
          <p className="text-sm text-slate-600 mt-1">Crea la tua prima pratica di conformità edilizia.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.bozza;
            const Icon = meta.icon;
            return (
              <div
                key={p.id}
                data-testid="pratica-card-item"
                onClick={() => navigate(`/pratica/${p.id}`)}
                className="group p-5 rounded-xl bg-[#121824] border border-slate-800 hover:border-cyan-500/40 transition-all cursor-pointer relative"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2" data-testid="pratica-status-badge">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.dot }} />
                    <span className={`font-mono-t text-[11px] uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
                  </div>
                  <button
                    data-testid="delete-pratica-button"
                    onClick={(e) => { e.stopPropagation(); deleteMut.mutate(p.id); }}
                    className="text-slate-600 hover:text-rose-400 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <h3 className="font-heading font-semibold text-lg mt-3 group-hover:text-cyan-300 transition-colors">{p.titolo}</h3>
                {p.indirizzo && (
                  <p className="flex items-center gap-1.5 text-sm text-slate-500 mt-1.5">
                    <MapPin className="h-3.5 w-3.5" /> {p.indirizzo}
                  </p>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800/80">
                  {p.analysis ? (
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${meta.text}`} />
                      <span className={`font-heading font-bold ${meta.text}`}>{p.analysis.discrepancy_percentage}%</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-600">Analisi non eseguita</span>
                  )}
                  <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
